import { getStoredAuthToken } from './api';
import { DEFAULT_SITE, type SiteId } from './sites';

export const ASK_EVENT_TYPES = ['token', 'citations', 'done', 'error'] as const;
export type AskEventType = (typeof ASK_EVENT_TYPES)[number];

export interface AskCitation {
  id: number;
  chunk_id?: string | null;
  slug: string;
  title: string;
  heading?: string | null;
  anchor?: string | null;
  snippet?: string | null;
}

export interface AskTokenEvent {
  type: 'token';
  content?: string;
  token?: string;
  query_id?: string;
  answer_id?: string;
}

export interface AskCitationsEvent {
  type: 'citations';
  citations: AskCitation[];
  query_id?: string;
  answer_id?: string;
}

export interface AskDoneEvent {
  type: 'done';
  query_id?: string;
  answer_id?: string;
  latency_ms?: number;
}

export interface AskErrorEvent {
  type: 'error';
  content?: string;
}

export type AskStreamEvent = AskTokenEvent | AskCitationsEvent | AskDoneEvent | AskErrorEvent;

export interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface AskPageContext {
  slug: string;
  title?: string;
  heading?: string;
}

export interface StreamAskRequest {
  query: string;
  site?: SiteId;
  conversationHistory?: ConversationTurn[];
  pageContext?: AskPageContext | null;
  token?: string | null;
  getAccessToken?: () => Promise<string | null>;
  signal?: AbortSignal;
  onEvent: (event: AskStreamEvent) => void;
}

export class AssistantApiError extends Error {
  readonly status: number;
  readonly aborted: boolean;

  constructor(message: string, status = 0, aborted = false) {
    super(message);
    this.name = 'AssistantApiError';
    this.status = status;
    this.aborted = aborted;
  }
}

const ASK_EVENT_TYPE_SET = new Set<string>(ASK_EVENT_TYPES);

function authHeaders(token?: string | null): Record<string, string> {
  const effectiveToken = token || getStoredAuthToken();
  const headers: Record<string, string> = {};
  if (effectiveToken) {
    headers.Authorization = `Bearer ${effectiveToken}`;
  }
  return headers;
}

async function readErrorDetail(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === 'string') return body.detail;
    if (Array.isArray(body?.detail) && body.detail[0]?.msg) {
      return String(body.detail[0].msg);
    }
    if (typeof body?.message === 'string') return body.message;
  } catch {
    try {
      const text = await res.text();
      if (text) return text;
    } catch {
      // ignore
    }
  }
  return fallback;
}

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: string }).name;
  return name === 'AbortError';
}

/**
 * Parse one SSE event block (without the trailing blank line).
 * Supports LF and CRLF field lines and multiple `data:` lines.
 */
export function parseSseBlock(block: string): { event?: string; data: string; id?: string } | null {
  const normalized = block.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const dataLines: string[] = [];
  let event: string | undefined;
  let id: string | undefined;

  for (const line of normalized.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'data') dataLines.push(value);
    else if (field === 'event') event = value;
    else if (field === 'id') id = value;
  }

  if (dataLines.length === 0 && event === undefined && id === undefined) {
    return null;
  }
  return { event, data: dataLines.join('\n'), id };
}

export type DecodedAskSse =
  | { kind: 'event'; event: AskStreamEvent }
  | { kind: 'done_marker' }
  | { kind: 'ignored' };

/**
 * Decode a completed SSE `data` payload into a typed Ask event.
 * Malformed JSON and unknown types are ignored — never treated as answer text.
 */
export function decodeAskSseData(data: string): DecodedAskSse {
  const trimmed = data.trim();
  if (!trimmed) return { kind: 'ignored' };
  if (trimmed === '[DONE]') return { kind: 'done_marker' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { kind: 'ignored' };
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { kind: 'ignored' };
  }

  const type = (parsed as { type?: unknown }).type;
  if (typeof type !== 'string' || !ASK_EVENT_TYPE_SET.has(type)) {
    return { kind: 'ignored' };
  }

  return { kind: 'event', event: parsed as AskStreamEvent };
}

export interface SseParser {
  push(chunk: string): void;
  flush(): void;
}

/**
 * Incremental SSE framing parser. Holds a remainder across arbitrary chunk
 * boundaries and splits on blank lines (`\n\n` or `\r\n\r\n`).
 */
export function createSseParser(onBlock: (block: { event?: string; data: string; id?: string }) => void): SseParser {
  let buffer = '';

  const emitComplete = (raw: string) => {
    const parsed = parseSseBlock(raw);
    if (parsed) onBlock(parsed);
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      while (true) {
        const match = buffer.match(/\r?\n\r?\n/);
        if (!match || match.index === undefined) break;
        const raw = buffer.slice(0, match.index);
        buffer = buffer.slice(match.index + match[0].length);
        if (raw.trim().length > 0) emitComplete(raw);
      }
    },
    flush() {
      if (buffer.trim().length > 0) {
        emitComplete(buffer);
      }
      buffer = '';
    },
  };
}

export function tokenTextFromEvent(event: AskTokenEvent): string {
  return event.content ?? event.token ?? '';
}

export function isAskTerminalEvent(event: AskStreamEvent): boolean {
  return event.type === 'done' || event.type === 'error';
}

export async function closeSseReader(reader: ReadableStreamDefaultReader<Uint8Array>): Promise<void> {
  try {
    await reader.cancel();
  } catch {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

export async function streamAsk(request: StreamAskRequest): Promise<void> {
  const { query, site = DEFAULT_SITE.id, conversationHistory, pageContext, signal, onEvent } = request;

  let token = request.token ?? null;
  if (!token && request.getAccessToken) {
    try {
      token = await request.getAccessToken();
    } catch {
      token = null;
    }
  }

  const body: Record<string, unknown> = { query, site };
  if (conversationHistory && conversationHistory.length > 0) {
    body.conversation_history = conversationHistory;
  }
  if (pageContext?.slug) {
    body.page_context = {
      slug: pageContext.slug,
      ...(pageContext.title ? { title: pageContext.title } : {}),
      ...(pageContext.heading ? { heading: pageContext.heading } : {}),
    };
  }

  let response: Response;
  try {
    response = await fetch('/api/v1/ask', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...authHeaders(token),
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) {
      throw new AssistantApiError('Request aborted', 0, true);
    }
    throw new AssistantApiError(
      error instanceof Error ? error.message : 'Failed to reach the assistant',
      0
    );
  }

  if (!response.ok) {
    throw new AssistantApiError(
      await readErrorDetail(response, `Assistant request failed (${response.status})`),
      response.status
    );
  }

  if (!response.body) {
    throw new AssistantApiError('Assistant response did not include a stream', response.status);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let reachedDone = false;

  const parser = createSseParser((block) => {
    const decoded = decodeAskSseData(block.data);
    if (decoded.kind === 'done_marker') {
      reachedDone = true;
      return;
    }
    if (decoded.kind === 'event') {
      onEvent(decoded.event);
      if (isAskTerminalEvent(decoded.event)) {
        reachedDone = true;
      }
    }
  });

  try {
    while (!reachedDone) {
      const { done, value } = await reader.read();
      if (done) {
        parser.flush();
        break;
      }
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.flush();
    if (!reachedDone) {
      throw new AssistantApiError('Assistant stream ended before completion');
    }
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) {
      throw new AssistantApiError('Request aborted', 0, true);
    }
    throw error instanceof AssistantApiError
      ? error
      : new AssistantApiError(error instanceof Error ? error.message : 'Assistant stream failed');
  } finally {
    await closeSseReader(reader);
  }
}
